"use client";

import { ReactNode, isValidElement, Children, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import * as Dialog from "@radix-ui/react-dialog";
import { X, ArrowRight, Check, ChevronDown, ShieldCheck } from "lucide-react";
import { Input, Label, Textarea, Field, Select } from "@/components/ui/Fields";
import { Stepper } from "@/components/ui/Stepper";
import { Button } from "@/components/ui/Button";
import { formatPrice, formatMoney, cn } from "@/lib/format";
import { media } from "@/lib/media";
import countryCodeData from "@/lib/country-code.json";
import type { GroupPrice, PackageTier, PackageAddon, Media } from "@/lib/dto";
import type { NormalizedTier } from "@/lib/trip/normalize";
import {
  useBookingWizardForm,
  STEP_LABELS,
  TOTAL_STEPS,
  BOOKING_CREATE_UI,
  FIND_US_OPTIONS,
  MONTH_OPTIONS,
} from "./useBookingWizardForm";

type Props = {
  trigger: ReactNode;
  packageTitle: string;
  packageSlug: string;
  /** Numeric package id — required by the `/api/booking` contract. */
  packageId?: number;
  /** The specific departure being booked, when one was picked. */
  departureId?: number;
  currency: string;
  /**
   * The tier being booked, already normalized by the caller. Carries the
   * tier's own `defaultPrice` + `groupPrices`, which are the authoritative
   * pricing inputs for a tier-priced package — the package-level
   * `pricing.group_prices` is empty in that case.
   */
  tier?: NormalizedTier | null;
  startDate?: Date | null;
  travelers?: number;
  pricing?: {
    group_prices: GroupPrice[];
    tiers: Record<string, PackageTier> | null;
  };
  addons?: PackageAddon[];
  featuredImage?: Media | null;
  duration?: number;
  durationUnit?: string;
  /**
   * Open the dialog immediately on mount. Used by BookingWizardLauncher: the
   * launcher shows the trigger eagerly and only lazy-loads this component on
   * first click, so once it does mount it needs to open itself rather than
   * waiting for a second click the user already made.
   */
  defaultOpen?: boolean;
};

export function BookingWizard({
  trigger,
  packageTitle,
  packageSlug,
  packageId,
  departureId,
  currency,
  tier,
  startDate,
  travelers: initialTravelers,
  pricing,
  addons: rawAddons,
  featuredImage,
  duration,
  durationUnit,
  defaultOpen = false,
}: Props) {
  const [open, setOpen] = useState(defaultOpen);
  const [showOptionalFields, setShowOptionalFields] = useState(false);

  const {
    currentStep,
    stepHeadingRef,
    addons,
    selectedDate,
    travelers,
    setTravelers,
    selectedAddons,
    setSelectedAddons,
    name,
    setName,
    email,
    setEmail,
    nationality,
    setNationality,
    countryCode,
    setCountryCode,
    mobileNumber,
    setMobileNumber,
    passportNo,
    setPassportNo,
    gender,
    setGender,
    day,
    setDay,
    month,
    setMonth,
    year,
    setYear,
    findingUs,
    setFindingUs,
    customFindingUs,
    setCustomFindingUs,
    message,
    setMessage,
    step2Errors,
    paymentPreference,
    setPaymentPreference,
    terms,
    setTerms,
    yearOptions,
    dayList,
    state,
    pending,
    redirecting,
    resetToStepOne,
    validateFieldOnBlur,
    nextStep,
    prevStep,
    groupPrices,
    pricePerPerson,
    tierLabel,
    effectiveTierLabel,
    tripTotal,
    grandTotal,
    depositAmount,
    bankCharge,
    depositEstimate,
    endDate,
    handleSubmit,
  } = useBookingWizardForm({
    packageTitle,
    packageSlug,
    packageId,
    departureId,
    currency,
    tier,
    startDate,
    travelers: initialTravelers,
    pricing,
    addons: rawAddons,
    duration,
    durationUnit,
    open,
  });

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) {
      // Reset the wizard so the next open starts fresh on step 1.
      resetToStepOne();
    }
  };

  const asChild = Children.count(trigger) === 1 && isValidElement(trigger);
  const headerImage = media(featuredImage ?? null);

  const countryOptions = countryCodeData.map((c) => ({
    value: c.name,
    label: c.name,
  }));
  const dialCodeOptions = countryCodeData.map((c) => ({
    name: c.name,
    value: c.dial_code,
    label: `${c.code} (${c.dial_code})`,
  }));

  return (
    <Dialog.Root open={open} onOpenChange={handleOpenChange}>
      {asChild ? (
        <Dialog.Trigger asChild>{trigger}</Dialog.Trigger>
      ) : (
        <Dialog.Trigger asChild>
          <button type="button">{trigger ?? "Book Now"}</button>
        </Dialog.Trigger>
      )}

      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm data-[state=open]:animate-in data-[state=open]:fade-in" />
        <Dialog.Content className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="relative flex max-h-[92vh] w-full max-w-5xl flex-col overflow-hidden rounded-sm bg-white shadow-2xl duration-200 data-[state=open]:animate-in data-[state=open]:fade-in data-[state=open]:zoom-in-95 data-[state=open]:slide-in-from-bottom-2">
            <Dialog.Close
              aria-label="Close"
              className="absolute right-4 top-4 z-40 rounded-full p-1.5 text-white/60 transition-colors hover:bg-white/10 hover:text-white"
            >
              <X className="h-5 w-5" strokeWidth={2} aria-hidden />
            </Dialog.Close>

            {/* Header — dark band: package thumbnail + title/tier badge on one
                row, numbered step rail beneath. */}
            <div className="shrink-0 rounded-t-sm bg-headings px-6 py-5 text-white sm:px-8 sm:py-6">
              <div className="flex items-center gap-3.5 pr-10">
                {headerImage && (
                  <div className="relative h-20 w-20 shrink-0 overflow-hidden rounded-sm ring-2 ring-white/25">
                    <Image
                      src={headerImage.src}
                      alt={headerImage.alt || packageTitle}
                      fill
                      sizes="200px"
                      className="object-cover"
                    />
                  </div>
                )}
                <div className="min-w-0">
                  <Dialog.Title className="truncate text-lg font-bold leading-tight text-white sm:text-xl">
                    {packageTitle}
                  </Dialog.Title>
                  <div className="mt-1.5 flex flex-wrap items-center gap-x-2.5 gap-y-1 text-xs text-white/60">
                    {tierLabel && (
                      <span className="inline-flex items-center rounded-full bg-primary px-2.5 py-0.5 text-[10px] font-extrabold uppercase tracking-wider text-white">
                        {tierLabel}
                      </span>
                    )}
                    {pricePerPerson > 0 && (
                      <span className="font-semibold tabular-nums text-white/80">
                        {formatPrice(pricePerPerson, currency)}
                        <span className="ml-0.5 font-medium text-white/50">
                          / person
                        </span>
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <ol className="mt-6 flex items-center gap-2">
                {STEP_LABELS.map((label, i) => {
                  const step = i + 1;
                  const done = currentStep > step;
                  const active = currentStep === step;
                  return (
                    <li
                      key={label}
                      className="flex flex-1 items-center gap-2.5"
                      aria-current={active ? "step" : undefined}
                    >
                      <span
                        className={cn(
                          "flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[11px] font-extrabold transition-colors",
                          done && "bg-primary text-white",
                          active && "bg-white text-headings",
                          !done && !active && "bg-white/15 text-white/50",
                        )}
                      >
                        {done ? (
                          <Check className="h-3.5 w-3.5" aria-hidden />
                        ) : (
                          step
                        )}
                      </span>
                      <span
                        className={cn(
                          "hidden truncate text-xs font-semibold sm:block",
                          active ? "text-white" : "text-white/50",
                        )}
                      >
                        {label}
                      </span>
                      {step < TOTAL_STEPS && (
                        <span
                          className={cn(
                            "ml-auto hidden h-px flex-1 sm:block",
                            done ? "bg-primary" : "bg-white/15",
                          )}
                          aria-hidden
                        />
                      )}
                    </li>
                  );
                })}
              </ol>
            </div>

            <form
              onSubmit={handleSubmit}
              noValidate
              className="flex-1 overflow-y-auto rounded-b-lg"
            >
              {/* Honeypot — bots fill this, humans never see it. */}
              <input
                type="hidden"
                name="website"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden
              />

              <div className="flex h-full flex-col lg:flex-row">
                {/* Main content */}
                <div className="flex-1 min-h-[60vh] p-6 sm:p-8">
                  {/* Step 1: Package Options */}
                  {currentStep === 1 && (
                    <div className="animate-in fade-in slide-in-from-right-4 space-y-7 duration-300">
                      <h3
                        ref={stepHeadingRef}
                        tabIndex={-1}
                        className="sr-only"
                      >
                        Step 1 of {TOTAL_STEPS}: {STEP_LABELS[0]} options
                      </h3>
                      <section>
                        <h3 className="mb-1.5 text-xs font-extrabold uppercase tracking-wider text-muted">
                          Departure
                        </h3>
                        <div className="flex items-center gap-3 shadow-2xs rounded border border-warning/20 bg-warning/5 px-4 py-2.5 text-xs font-semibold text-headings">
                          {selectedDate ? (
                            <>
                              <span>
                                {selectedDate.toLocaleDateString("en-US", {
                                  month: "short",
                                  day: "numeric",
                                  year: "numeric",
                                })}
                              </span>
                              {endDate && (
                                <>
                                  <ArrowRight
                                    className="h-3.5 w-3.5 opacity-40"
                                    aria-hidden
                                  />
                                  <span>
                                    {endDate.toLocaleDateString("en-US", {
                                      month: "short",
                                      day: "numeric",
                                      year: "numeric",
                                    })}
                                  </span>
                                </>
                              )}
                              {duration && (
                                <span className="ml-auto text-xs font-medium text-muted">
                                  {duration} {durationUnit || "days"}
                                </span>
                              )}
                            </>
                          ) : (
                            <span className="text-muted">
                              No date selected yet
                            </span>
                          )}
                        </div>
                      </section>

                      {/* Traveller count */}
                      <section>
                        <h3 className="mb-3 text-xs font-extrabold uppercase tracking-wider text-muted">
                          Travellers
                        </h3>
                        <div className="flex items-center justify-between gap-3 rounded-sm border border-line px-4 py-3.5">
                          <div className="min-w-0">
                            <p className="text-md font-semibold text-headings">
                              {travelers} traveller
                              {travelers !== 1 ? "s" : ""}
                            </p>
                            {pricePerPerson > 0 && (
                              <p className="mt-0.5 text-xs tabular-nums text-headings/80">
                                <b className="font-bold text-primary">
                                  {formatPrice(pricePerPerson, currency)}
                                </b>{" "}
                                per person
                              </p>
                            )}
                          </div>
                          <Stepper
                            value={travelers}
                            onChange={setTravelers}
                            min={1}
                            max={99}
                          />
                        </div>

                        {groupPrices.length > 0 && (
                          <div className="mt-3 rounded-sm border border-line">
                            <span className="block border-b border-line px-3 py-2 text-[10px] font-bold uppercase tracking-wide text-muted">
                              Group rates — price per person
                              {effectiveTierLabel && (
                                <span className="ml-1 normal-case text-muted-soft">
                                  ({effectiveTierLabel})
                                </span>
                              )}
                            </span>
                            <ul className="divide-y divide-line">
                              {groupPrices.map((gp, i) => (
                                <li
                                  key={i}
                                  className={cn(
                                    "flex justify-between px-3 py-2 text-xs tabular-nums",
                                    travelers >= gp.minPerson &&
                                      travelers <= gp.maxPerson
                                      ? "bg-primary/10 font-bold text-primary"
                                      : "text-muted",
                                  )}
                                >
                                  <span>
                                    {gp.minPerson}–{gp.maxPerson} pax
                                  </span>
                                  <span>{formatPrice(gp.price, currency)}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </section>

                      {/* Add-ons */}
                      {addons.length > 0 && (
                        <section>
                          <h3 className="mb-3 text-xs font-extrabold uppercase tracking-wider text-muted">
                            Add-ons
                          </h3>
                          <div className="space-y-2.5">
                            {addons.map((addon) => (
                              <div
                                key={addon.id}
                                className={cn(
                                  "flex items-center gap-4 rounded-sm border px-4 py-3.5 transition-colors",
                                  (selectedAddons[addon.id] ?? 0) > 0
                                    ? "border-primary/40 bg-primary/[0.04]"
                                    : "border-line hover:border-primary/40",
                                )}
                              >
                                <div className="min-w-0 flex-1">
                                  <div className="flex items-end gap-2">
                                    <span className="truncate text-sm font-semibold text-headings">
                                      {addon.title}
                                    </span>
                                    <span className="ml-auto leading-[1.1] text-right shrink-0 text-sm font-bold tabular-nums text-headings">
                                      {formatPrice(addon.price, currency)}
                                      {addon.unit && (
                                        <i className="text-[11px] tracking-wide block text-muted-soft font-medium">
                                          {addon.unit}
                                        </i>
                                      )}
                                    </span>
                                  </div>

                                  {addon.description && (
                                    <p className="mt-2.5 text-xs text-muted">
                                      {addon.description}
                                    </p>
                                  )}
                                </div>
                                <Stepper
                                  value={selectedAddons[addon.id] ?? 0}
                                  onChange={(val) =>
                                    setSelectedAddons((prev) => ({
                                      ...prev,
                                      [addon.id]: val,
                                    }))
                                  }
                                  min={0}
                                  max={99}
                                />
                              </div>
                            ))}
                          </div>
                        </section>
                      )}
                    </div>
                  )}

                  {/* Step 2: Participant Info */}
                  {currentStep === 2 && (
                    <div className="animate-in fade-in slide-in-from-right-4 duration-300">
                      <h3
                        ref={stepHeadingRef}
                        tabIndex={-1}
                        className="mb-5 text-lg font-bold text-headings"
                      >
                        Lead Traveller
                      </h3>
                      <div className="space-y-6">
                        <Field>
                          <Label htmlFor="full_name">Full name</Label>
                          <Input
                            id="full_name"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            onBlur={() => validateFieldOnBlur("name")}
                            error={!!step2Errors.name}
                            aria-describedby={
                              step2Errors.name ? "full_name-err" : undefined
                            }
                          />
                          {step2Errors.name && (
                            <p
                              id="full_name-err"
                              role="alert"
                              className="mt-1.5 text-xs text-danger"
                            >
                              {step2Errors.name}
                            </p>
                          )}
                        </Field>

                        <Field>
                          <Label htmlFor="email_addr">Email address</Label>
                          <Input
                            id="email_addr"
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            onBlur={() => validateFieldOnBlur("email")}
                            error={!!step2Errors.email}
                            aria-describedby={
                              step2Errors.email ? "email_addr-err" : undefined
                            }
                          />
                          {step2Errors.email && (
                            <p
                              id="email_addr-err"
                              role="alert"
                              className="mt-1.5 text-xs text-danger"
                            >
                              {step2Errors.email}
                            </p>
                          )}
                        </Field>

                        <Field>
                          <Label htmlFor="mobile_number">Mobile number</Label>
                          <div className="grid grid-cols-3 gap-1.5">
                            <Select
                              aria-label="Country code"
                              value={countryCode}
                              onChange={(e) => setCountryCode(e.target.value)}
                              onBlur={() => validateFieldOnBlur("mobileNumber")}
                              className="shrink-0"
                            >
                              <option value="">Code</option>
                              {dialCodeOptions.map((c) => (
                                <option key={c.name} value={c.value}>
                                  {c.label}
                                </option>
                              ))}
                            </Select>
                            <Input
                              id="mobile_number"
                              type="tel"
                              className="col-span-2"
                              value={mobileNumber}
                              onChange={(e) => setMobileNumber(e.target.value)}
                              onBlur={() => validateFieldOnBlur("mobileNumber")}
                              placeholder="1234567890"
                              error={!!step2Errors.mobileNumber}
                              aria-describedby={
                                step2Errors.mobileNumber
                                  ? "mobile_number-err"
                                  : undefined
                              }
                            />
                          </div>
                          {step2Errors.mobileNumber && (
                            <p
                              id="mobile_number-err"
                              role="alert"
                              className="mt-1.5 text-xs text-danger"
                            >
                              {step2Errors.mobileNumber}
                            </p>
                          )}
                        </Field>

                        <Field>
                          <Label htmlFor="nationality">Nationality</Label>
                          <Select
                            id="nationality"
                            value={nationality}
                            onChange={(e) => setNationality(e.target.value)}
                            onBlur={() => validateFieldOnBlur("nationality")}
                            error={!!step2Errors.nationality}
                            aria-describedby={
                              step2Errors.nationality
                                ? "nationality-err"
                                : undefined
                            }
                          >
                            <option value="">Select country</option>
                            {countryOptions.map((c) => (
                              <option key={c.value} value={c.value}>
                                {c.label}
                              </option>
                            ))}
                          </Select>
                          {step2Errors.nationality && (
                            <p
                              id="nationality-err"
                              role="alert"
                              className="mt-1.5 text-xs text-danger"
                            >
                              {step2Errors.nationality}
                            </p>
                          )}
                        </Field>

                        <div className="border-t border-line pt-5">
                          <button
                            type="button"
                            onClick={() => setShowOptionalFields((v) => !v)}
                            aria-expanded={showOptionalFields}
                            className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary hover:text-primary-dark"
                          >
                            {showOptionalFields
                              ? "Hide optional details"
                              : "Add more details (optional)"}
                            <ChevronDown
                              size={14}
                              aria-hidden
                              className={cn(
                                "transition-transform",
                                showOptionalFields && "rotate-180",
                              )}
                            />
                          </button>

                          {showOptionalFields && (
                            <div className="mt-5 space-y-6">
                              <Field>
                                <Label htmlFor="passport_no">
                                  Passport number (optional)
                                </Label>
                                <Input
                                  id="passport_no"
                                  value={passportNo}
                                  onChange={(e) =>
                                    setPassportNo(e.target.value)
                                  }
                                  onBlur={() =>
                                    validateFieldOnBlur("passportNo")
                                  }
                                  placeholder="Passport No. — we'll need this before departure"
                                  error={!!step2Errors.passportNo}
                                  aria-describedby={
                                    step2Errors.passportNo
                                      ? "passport_no-err"
                                      : undefined
                                  }
                                />
                                {step2Errors.passportNo && (
                                  <p
                                    id="passport_no-err"
                                    role="alert"
                                    className="mt-1.5 text-xs text-danger"
                                  >
                                    {step2Errors.passportNo}
                                  </p>
                                )}
                              </Field>

                              <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
                                <div className="sm:col-span-1">
                                  <Label>Gender (optional)</Label>
                                  <Select
                                    aria-label="Gender"
                                    value={gender}
                                    onChange={(e) => setGender(e.target.value)}
                                    onBlur={() => validateFieldOnBlur("gender")}
                                    error={!!step2Errors.gender}
                                    aria-describedby={
                                      step2Errors.gender
                                        ? "gender-err"
                                        : undefined
                                    }
                                  >
                                    <option value="">Gender</option>
                                    <option value="Male">Male</option>
                                    <option value="Female">Female</option>
                                    <option value="Other">Other</option>
                                  </Select>

                                  {step2Errors.gender && (
                                    <p
                                      id="gender-err"
                                      role="alert"
                                      className="mt-1.5 text-xs text-danger"
                                    >
                                      {step2Errors.gender}
                                    </p>
                                  )}
                                </div>
                                <div className="sm:col-span-2">
                                  <Label>Date of birth (optional)</Label>
                                  <div className="grid grid-cols-3 gap-1.5">
                                    <Select
                                      aria-label="Day"
                                      value={day}
                                      onChange={(e) => setDay(e.target.value)}
                                      onBlur={() => validateFieldOnBlur("dob")}
                                      error={!!step2Errors.dob}
                                      aria-describedby={
                                        step2Errors.dob ? "dob-err" : undefined
                                      }
                                    >
                                      <option value="">Day</option>
                                      {dayList.map((d) => (
                                        <option key={d} value={d}>
                                          {d}
                                        </option>
                                      ))}
                                    </Select>
                                    <Select
                                      aria-label="Month"
                                      value={month}
                                      onChange={(e) => setMonth(e.target.value)}
                                      onBlur={() => validateFieldOnBlur("dob")}
                                      error={!!step2Errors.dob}
                                      aria-describedby={
                                        step2Errors.dob ? "dob-err" : undefined
                                      }
                                    >
                                      <option value="">Month</option>
                                      {MONTH_OPTIONS.map((m) => (
                                        <option key={m.value} value={m.value}>
                                          {m.label}
                                        </option>
                                      ))}
                                    </Select>
                                    <Select
                                      aria-label="Year"
                                      value={year}
                                      onChange={(e) => setYear(e.target.value)}
                                      onBlur={() => validateFieldOnBlur("dob")}
                                      error={!!step2Errors.dob}
                                      aria-describedby={
                                        step2Errors.dob ? "dob-err" : undefined
                                      }
                                    >
                                      <option value="">Year</option>
                                      {yearOptions.map((y) => (
                                        <option key={y} value={y}>
                                          {y}
                                        </option>
                                      ))}
                                    </Select>
                                  </div>
                                  {step2Errors.dob && (
                                    <p
                                      id="dob-err"
                                      role="alert"
                                      className="mt-1.5 text-xs text-danger"
                                    >
                                      {step2Errors.dob}
                                    </p>
                                  )}
                                </div>
                              </div>

                              <Field>
                                <Label htmlFor="special_requests">
                                  Special requests
                                </Label>
                                <Textarea
                                  id="special_requests"
                                  value={message}
                                  onChange={(e) => setMessage(e.target.value)}
                                  placeholder="Vegetarian meal, food allergies, extra bed, etc."
                                  rows={3}
                                />
                              </Field>

                              <Field>
                                <Label htmlFor="finding_us">
                                  How did you find us?
                                </Label>
                                <Select
                                  id="finding_us"
                                  value={findingUs}
                                  onChange={(e) => setFindingUs(e.target.value)}
                                >
                                  <option value="">Select option</option>
                                  {FIND_US_OPTIONS.map((opt) => (
                                    <option key={opt} value={opt}>
                                      {opt}
                                    </option>
                                  ))}
                                </Select>
                                {findingUs === "Others" && (
                                  <Input
                                    className="mt-2"
                                    value={customFindingUs}
                                    onChange={(e) =>
                                      setCustomFindingUs(e.target.value)
                                    }
                                    placeholder="Please specify"
                                  />
                                )}
                              </Field>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Step 3: Payment */}
                  {currentStep === 3 && (
                    <div className="animate-in fade-in slide-in-from-right-4 duration-300">
                      <h3
                        ref={stepHeadingRef}
                        tabIndex={-1}
                        className="mb-5 text-lg font-bold text-headings"
                      >
                        Payment Option
                      </h3>
                      <div className="space-y-5">
                        <div className="space-y-2.5">
                          <label
                            className={cn(
                              "flex cursor-pointer items-center gap-3.5 rounded-sm border px-4 py-3.5 transition-colors",
                              paymentPreference === "partial_payment"
                                ? "border-primary bg-primary/[0.04]"
                                : "border-line hover:border-primary/40",
                            )}
                          >
                            <input
                              type="radio"
                              checked={paymentPreference === "partial_payment"}
                              onChange={() =>
                                setPaymentPreference("partial_payment")
                              }
                              className="shrink-0"
                            />
                            <div className="min-w-0 flex-1">
                              <span className="block text-sm font-bold text-headings">
                                Pay 20% deposit
                              </span>
                              <span className="block text-[11px] text-muted">
                                Balance due on arrival
                              </span>
                            </div>
                            <span className="shrink-0 text-sm font-extrabold tabular-nums text-headings">
                              {formatMoney(depositAmount, currency)}
                            </span>
                          </label>

                          <label
                            className={cn(
                              "flex cursor-pointer items-center gap-3.5 rounded-sm border px-4 py-3.5 transition-colors",
                              paymentPreference === "fully_payment"
                                ? "border-primary bg-primary/[0.04]"
                                : "border-line hover:border-primary/40",
                            )}
                          >
                            <input
                              type="radio"
                              checked={paymentPreference === "fully_payment"}
                              onChange={() =>
                                setPaymentPreference("fully_payment")
                              }
                              className="shrink-0"
                            />
                            <div className="min-w-0 flex-1">
                              <span className="block text-sm font-bold text-headings">
                                Pay in full
                              </span>
                              <span className="block text-[11px] text-muted">
                                Settle everything now
                              </span>
                            </div>
                            <span className="shrink-0 text-sm font-extrabold tabular-nums text-headings">
                              {formatMoney(grandTotal, currency)}
                            </span>
                          </label>
                        </div>

                        <label className="flex cursor-pointer items-start gap-2.5 border-t border-line pt-4">
                          <input
                            type="checkbox"
                            checked={terms}
                            onChange={(e) => setTerms(e.target.checked)}
                            className="mt-0.5 rounded text-primary"
                          />
                          <span className="text-xs leading-relaxed text-muted">
                            I verify that I have read and agree to the{" "}
                            <Link
                              href="/terms-and-conditions"
                              target="_blank"
                              className="font-medium text-primary hover:underline"
                            >
                              Terms &amp; Conditions
                            </Link>
                            .
                          </span>
                        </label>

                        {state && !state.ok ? (
                          <p
                            role="alert"
                            className="rounded-sm border border-danger/25 bg-danger/5 p-3 text-xs font-medium text-danger"
                          >
                            {state.message}
                          </p>
                        ) : null}
                      </div>
                    </div>
                  )}

                  {state?.ok && (
                    <div className="mt-4 space-y-1 rounded-sm border border-emerald-200 bg-emerald-50 p-4 text-center">
                      <Check
                        className="mx-auto h-6 w-6 text-emerald-600"
                        aria-hidden
                      />
                      <p className="text-sm font-bold text-emerald-900">
                        {redirecting
                          ? "Booking Confirmed!"
                          : "Booking Request Sent!"}
                      </p>
                      <p className="text-xs text-emerald-800">
                        {redirecting
                          ? "Taking you to the next step…"
                          : state.message}
                      </p>
                    </div>
                  )}
                </div>

                {/* Summary sidebar */}
                <div className="flex h-auto w-full shrink-0 flex-col border-t border-line bg-bg/40 lg:h-auto lg:w-[340px] lg:border-l lg:border-t-0">
                  <div className="p-6 sticky top-0">
                    <h3 className="mb-4 text-xs font-extrabold uppercase tracking-wider text-muted">
                      Your Booking
                    </h3>

                    <div className="space-y-3">
                      <div className="flex items-baseline justify-between gap-2 border-b border-line pb-4">
                        <span className="text-[11px] font-bold uppercase tracking-wide text-muted-soft">
                          Dates
                        </span>
                        <span className="text-right text-xs font-semibold text-headings">
                          {selectedDate
                            ? `${selectedDate.toLocaleDateString("en-US", { month: "short", day: "numeric" })}${
                                endDate
                                  ? ` – ${endDate.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`
                                  : ""
                              }`
                            : "Not selected"}
                        </span>
                      </div>

                      {tierLabel && (
                        <div className="flex items-baseline justify-between gap-2 border-b border-line pb-4">
                          <span className="text-[11px] font-bold uppercase tracking-wide text-muted-soft">
                            Package
                          </span>
                          <span className="text-right text-xs font-semibold capitalize text-primary">
                            {tierLabel}
                          </span>
                        </div>
                      )}

                      <div className="space-y-1.5 border-b border-line pb-1.5">
                        <div className="flex items-start justify-between gap-2 text-[13px]">
                          <p className="min-w-0">
                            <span className="line-clamp-2 text-headings font-semibold">
                              Trip Price
                            </span>
                            <span className="text-muted text-[11px]">
                              {formatMoney(pricePerPerson, currency)} ×{" "}
                              {travelers}
                            </span>
                          </p>
                          <span className="shrink-0 font-bold tabular-nums text-headings">
                            {formatMoney(tripTotal, currency)}
                          </span>
                        </div>
                        {addons.map((addon) => {
                          const qty = selectedAddons[addon.id] ?? 0;
                          if (!qty) return null;
                          return (
                            <div
                              key={addon.id}
                              className="flex items-start justify-between gap-2 text-xs"
                            >
                              <span className="min-w-0 truncate text-muted">
                                {addon.title}{" "}
                                <span className="text-muted-soft">× {qty}</span>
                              </span>
                              <span className="shrink-0 font-bold tabular-nums text-headings">
                                {formatMoney(addon.price * qty, currency)}
                              </span>
                            </div>
                          );
                        })}
                      </div>

                      {/* <div className="flex items-center justify-between border-b border-line pb-4">
                        <span className="text-xs font-bold uppercase tracking-wide text-muted-soft">
                          Trip Total
                        </span>
                        <span className="text-sm font-extrabold tabular-nums text-headings">
                          {formatMoney(grandTotal, currency)}
                        </span>
                      </div> */}

                      {paymentPreference === "partial_payment" && (
                        <div className="flex items-start justify-between gap-2 border-b border-line pb-3 text-[13px]">
                          <p className="min-w-0">
                            <span className="text-headings font-semibold">
                              Deposit Amount
                            </span>
                            <span className="block text-muted text-[11px]">
                              20% of total price
                            </span>
                          </p>
                          <span className="shrink-0 font-bold tabular-nums text-headings">
                            {formatMoney(depositAmount, currency)}
                          </span>
                        </div>
                      )}

                      <div className="flex items-start justify-between gap-2 border-b border-line pb-3 text-[13px]">
                        <p className="min-w-0">
                          <span className="text-headings font-semibold">
                            Bank Charge
                          </span>
                          <span className="block text-muted text-[11px]">
                            3.5% Card Processing Fee
                          </span>
                        </p>
                        <span className="shrink-0 font-bold tabular-nums text-headings">
                          {formatMoney(bankCharge, currency)}
                        </span>
                      </div>

                      <div>
                        <div className="flex items-baseline justify-between gap-2">
                          <span className="text-xs font-bold text-headings">
                            Total Payable Now
                          </span>
                          <span className="text-xl font-extrabold tabular-nums text-headings">
                            {formatMoney(depositEstimate, currency)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Actions footer */}
                  <div className="mt-auto border-t border-line bg-white p-6">
                    {currentStep < TOTAL_STEPS ? (
                      <Button
                        type="button"
                        onClick={nextStep}
                        variant="primary"
                        className="w-full justify-center"
                      >
                        Continue
                      </Button>
                    ) : (
                      <Button
                        type="submit"
                        variant="primary"
                        disabled={!terms || pending || redirecting}
                        className="w-full justify-center gap-2"
                      >
                        {pending || redirecting ? (
                          "Processing…"
                        ) : (
                          <>
                            <Check size={16} aria-hidden />
                            {BOOKING_CREATE_UI
                              ? "Confirm & Continue to Payment"
                              : "Send Booking Request"}
                          </>
                        )}
                      </Button>
                    )}

                    {currentStep > 1 && (
                      <button
                        type="button"
                        onClick={prevStep}
                        className="mt-2 w-full py-1.5 text-xs font-semibold text-muted transition-colors hover:text-headings"
                      >
                        Back
                      </button>
                    )}

                    <div className="mt-3 flex-col flex items-center justify-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-muted-soft">
                      <span className="w-[180px]">
                        <Image
                          alt="We Accept"
                          loading="lazy"
                          width={302}
                          height={33}
                          src="/payment.png"
                        />
                      </span>
                      <p>Secure SSL Encrypted</p>
                    </div>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
